import '../sentry.client.config';
import '../styles/globals.css';
import { CssBaseline, ThemeProvider } from '@mui/material';
import type { AppProps } from 'next/app';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { Provider } from 'jotai';
import dynamic from 'next/dynamic';
import React, { useMemo } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import {
  ApiError,
  Policy,
  AppNameProvider,
  Forbidden,
  ErrorFallback,
  Layout,
} from 'ui';
import { getVersion } from '../utils/version';
import useUser from '../auth/useUser';
import { APP_NAME } from '../appName';
import { ErrorBoundary } from 'react-error-boundary';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import PaymentsIcon from '@mui/icons-material/Payments';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import TaskOutlinedIcon from '@mui/icons-material/TaskOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import theme from '../styles/theme';
import { useRouter } from 'next/router';
import DataTreatmentModalGDC from '../components/DataTreatmentModal/DataTreatmentModalGDC';

// We load this component dynamically to disable SSR on it because it depends on the value of the window object.
const AuthContextProvider = dynamic(
  () => {
    return import('../auth/AuthContext');
  },
  { ssr: false }
);

const errorFunction = (error: unknown) => {
  if (error instanceof ApiError) {
    console.error(error, {
      errorCode: error.errorCode,
      appName: error.appName,
      ctx: error.ctx,
      cause: error.cause,
      requestId: error.requestId,
      errorId: error.errorId,
    });
    toast.error(
      `Error: ${error.message}\nCódigo: ${error.errorCode}\n[${error.errorId}]`,
      {
        duration: 5000,
      }
    );
    return;
  }

  let realError = error;
  if (!(error instanceof Error)) {
    realError = new Error(JSON.stringify(error));
  }

  console.error(realError);
  toast.error(`Error: ${(realError as Error).message}`);
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: errorFunction,
  }),
  mutationCache: new MutationCache({
    onError: errorFunction,
  }),
});

type ExtendedAppProps = AppProps & {
  Component: AppProps['Component'] & { skipAuth?: boolean };
};

type CompanyContext = {
  termsChecked: boolean;
};

type CompanyScopedRenderer = (context: CompanyContext) => React.ReactNode;

const companyScopedRenderers: Partial<Record<string, CompanyScopedRenderer[]>> =
  {
    gascaribe: [
      ({ termsChecked }) => (
        <DataTreatmentModalGDC
          termsChecked={termsChecked}
          key="data-treatment-modal"
        />
      ),
    ],
  };

/**
 * Entrada de menú de Pronto Pago. La vista del proveedor (sus solicitudes) y la interna de
 * Tesorería y Cuentas por Pagar (todas las solicitudes) comparten la misma ruta: la página
 * decide cuál mostrar según el permiso del usuario.
 */
function getPromptPaymentMenuOptions(
  company: string | string[],
  roles: string[]
) {
  const options: { title: string; path: string; icon: JSX.Element }[] = [];

  if (!roles.includes('mercury/manage_prompt_payment_direct')) {
    return options;
  }

  if (
    roles.includes('mercury/get_prompt_payment_direct_requests_provider') ||
    roles.includes('mercury/get_prompt_payment_direct_requests_internal')
  ) {
    options.push({
      title: 'Pronto Pago',
      path: `/${company}/prompt-payment-direct-request`,
      icon: <RequestQuoteOutlinedIcon color="primary" />,
    });
  }

  return options;
}

function ComponentWrapper({ Component, pageProps }: ExtendedAppProps) {
  const user = useUser();
  const router = useRouter();

  const { company } = router.query;
  const companyName = Array.isArray(company) ? company[0] : company;

  const termsChecked = user?.appMeta?.config?.termsChecked ?? false;

  const companyElements = useMemo(() => {
    if (!companyName) {
      return [];
    }

    const renderers = companyScopedRenderers[companyName] ?? [];
    return renderers.map((render) => render({ termsChecked }));
  }, [companyName, termsChecked]);

  const asideMenuOptions = useMemo(() => {
    if (!company) {
      return [];
    }

    const options = [];

    if (user?.roles.includes('mercury/manage_profile')) {
      options.push({
        title: 'Inicio',
        path: `/${company}`,
        icon: <DashboardIcon color="primary" />,
      });
    }

    if (user?.roles.includes('mercury/manage_certificates')) {
      options.push({
        title: 'Certificados',
        path: `/${company}/certificate-requests`,
        icon: <TaskOutlinedIcon color="primary" />,
      });
    }

    if (user?.roles.includes('mercury/manage_invoices')) {
      options.push({
        title: 'Facturas',
        path: `/${company}/invoices`,
        icon: <FileCopyOutlinedIcon color="primary" />,
      });
    }

    if (user?.roles.includes('mercury/manage_receivable_accounts')) {
      options.push({
        title: 'Cuentas por cobrar',
        path: `/${company}/receivable-accounts`,
        icon: <PaidOutlinedIcon color="primary" />,
      });
    }

    if (user?.roles.includes('mercury/manage_payments')) {
      options.push({
        title: 'Consolidado de pagos',
        path: `/${company}/payments`,
        icon: <PaymentsIcon color="primary" />,
      });
    }

    options.push(...getPromptPaymentMenuOptions(company, user?.roles ?? []));

    if (user?.roles.includes('mercury/manage_provider_nit_assignments')) {
      options.push({
        title: 'Configuraciones',
        path: `/${company}/configurations`,
        icon: <SettingsOutlinedIcon color="primary" />,
      });
    }

    return options;
  }, [company, user]);

  if (Component.skipAuth) {
    return (
      <React.StrictMode>
        <Component {...pageProps} />
      </React.StrictMode>
    );
  }

  // The AuthContextProvider (and the keycloak-js library) does not play well with StrictMode's unmounting and remounting of the components.
  // Running AuthContextProvider inside StrictMode causes an infinite redirect loop when coming back from the idP.
  return (
    <AuthContextProvider clientId="monorepo">
      <Policy
        user={user}
        roles={['login']}
        fallback={
          <Forbidden
            text={'No tienes permiso para acceder a la aplicación'}
            userLogout={user?.actions?.logout}
            email={user?.email}
          />
        }
      >
        <React.StrictMode>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Layout
              pageTitle="Mercury"
              userName={user?.name}
              noPageTitleHeader={true}
              customMainStyle={{
                top: '60px',
                minWidth: '410px',
                padding: theme.spacing(2),
              }}
              customHeaderStyle={{
                borderBottom: '1px solid #eeeeee',
                opacity: 1,
              }}
              asideMenuOptions={asideMenuOptions}
              customButtonsAsideStyle={{
                height: '82px',
                margin: '0',
                padding: '16px',
              }}
              enabledUserIcon={true}
              userLogout={user?.actions?.logout}
            >
              <Component {...pageProps} />
              {companyElements}
            </Layout>
          </ThemeProvider>
        </React.StrictMode>
      </Policy>
    </AuthContextProvider>
  );
}

function MyApp(props: ExtendedAppProps) {
  console.log('Version', getVersion());

  return (
    <ErrorBoundary FallbackComponent={() => <ErrorFallback />}>
      <AppNameProvider appName={APP_NAME}>
        <Provider>
          <Toaster />
          <QueryClientProvider client={queryClient}>
            <ComponentWrapper {...props} />
          </QueryClientProvider>
        </Provider>
      </AppNameProvider>
    </ErrorBoundary>
  );
}

export default MyApp;
